// SearchableSelect — a lightweight type-to-filter combobox (v1.85.1). Built for
// pickers backed by long lists (e.g. a tenant's Entra groups) where a native <select>
// is hard to navigate. Controlled: `value` is the selected option value; `onChange`
// fires with the chosen value (or '' when cleared). Options are `{ value, label }`.
// Keyboard: ↑/↓ move, Enter selects, Esc closes. Click-away closes.

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/24/outline';

export default function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  disabled = false,
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selectedLabel = useMemo(() => {
    const m = options.find((o) => o.value === value);
    return m ? m.label : '';
  }, [options, value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => (o.label || '').toLowerCase().includes(q));
  }, [options, query]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  useEffect(() => { setHighlight(0); }, [query, open]);

  const choose = (opt) => {
    onChange(opt ? opt.value : '');
    setQuery('');
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHighlight((h) => Math.min(h + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { if (open && filtered[highlight]) { e.preventDefault(); choose(filtered[highlight]); } }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          disabled={disabled}
          value={open ? query : selectedLabel}
          placeholder={selectedLabel || placeholder}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => { if (!disabled) { setOpen(true); setQuery(''); } }}
          onKeyDown={onKeyDown}
          className="input-field w-full pr-14 disabled:opacity-50"
        />
        <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
          {value && !disabled && (
            <button type="button" onClick={() => choose(null)} title="Clear" className="p-1 text-gray-500 hover:text-gray-300">
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
          <ChevronUpDownIcon className="h-4 w-4 text-gray-500 pointer-events-none" />
        </div>
      </div>
      {open && !disabled && (
        <div className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto bg-gray-900 border border-gray-700 rounded-md shadow-2xl">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-500">No matches</div>
          ) : (
            filtered.map((o, i) => (
              <button
                key={o.value}
                type="button"
                onMouseEnter={() => setHighlight(i)}
                onClick={() => choose(o)}
                className={`block w-full text-left px-3 py-2 text-sm truncate ${
                  i === highlight ? 'bg-blue-600/30 text-blue-100' : 'text-gray-300 hover:bg-gray-800'
                } ${o.value === value ? 'font-semibold' : ''}`}
                title={o.label}
              >
                {o.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
